import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  useTheme,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/system";
import { useGetTopCriticalRiskQuery } from "../../../store/slices/apis/riskDashboardSlice";
import { useParams } from "react-router-dom";
import PaginationComponent from "components/ui-components/PaginationComponent";

const StyledTableHead = styled(TableCell)({
  fontWeight: 500,
  fontSize: "14px",
  color: "#505258",
  padding: "10px 14px",
  backgroundColor: "#F8F8F8",
  border: "1px solid #D8D8D8",
  textAlign: "center",
  whiteSpace: "nowrap",
  lineHeight: "20px",
});

const StyledTableBody = styled(TableCell)({
  fontWeight: 500,
  fontSize: "14px",
  color: "#5052588A",
  padding: "10px 14px",
  backgroundColor: "#fff",
  border: "1px solid #D8D8D8",
  textAlign: "center",
  whiteSpace: "nowrap",
});

const StyledSkeletonCell = styled(TableCell)({
  padding: "10px 14px",
  border: "1px solid #D8D8D8",
  textAlign: "center",
  backgroundColor: "#fff",
});

const monthOptions = [
  { value: "2025-05-01", label: "May 2025" },
  { value: "2025-06-01", label: "June 2025" },
  { value: "2025-07-01", label: "July 2025" },
];

const TopCriticalRisk = () => {
  const { projectId } = useParams();
  const theme = useTheme();
  const [selectedMonth, setSelectedMonth] = useState("2025-05-01");
  const [currentPage, setCurrentPage] = useState(1);
  const { data: TopCriticalRisk = [], isLoading } = useGetTopCriticalRiskQuery({
    project_id: projectId,
  });

  const rowsPerPage = 5;
  const rawData = TopCriticalRisk?.data || TopCriticalRisk || [];
  const totalPages = Math.ceil(rawData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = rawData?.slice(startIndex, endIndex);

  const getBgColor = (value) => {
    switch (value) {
      case "Low":
        return "#006045";
      case "High":
        return "#EE4F3E";
      case "Medium":
        return "#F0BD39";
      case "Very High":
        return "#841F13";
      default:
        return "#fff";
    }
  };

  if (isLoading) {
    return (
      <Paper
        sx={{
          boxShadow: "none",
          marginTop: "20px",
          paddingBottom: "18px",
          borderRadius: "5px",
          minHeight: "400px",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[...Array(4)].map((_, i) => (
                  <StyledSkeletonCell key={i}>
                    <Box sx={{ py: 2 }}>
                      <Skeleton
                        variant="rectangular"
                        width={i === 0 ? 20 : 80}
                        height={15}
                      />
                    </Box>
                  </StyledSkeletonCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <TableRow key={index}>
                  {[...Array(4)].map((_, i) => (
                    <StyledSkeletonCell key={i}>
                      <Box sx={{ py: 1 }}>
                        <Skeleton
                          variant={i === 0 ? "circular" : "text"}
                          width={i === 0 ? 18 : "90%"}
                          height={20}
                        />
                      </Box>
                    </StyledSkeletonCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  return (
    <>
      <Box>
        <Box
          sx={{
            width: "100%",
            height: "424px",
            border: "1px solid #D8D8D8",
            mt: 0,
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: "white",
            position: "relative",
            pb: "76px",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "100%",
                letterSpacing: "-0.4px",
                color: `${theme?.customSettings?.themeColor}`,
              }}
            >
              Top Critical Risks
            </Typography>
            <Typography
              sx={{
                mt: "8px",
                color: "#5052588A",
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "-0.4px",
              }}
            >
              Identify high-impact risks to focus mitigation efforts and safeguard project outcomes.
            </Typography>
          </Box>
          <TableContainer>
            <Table sx={{ width: "100%", border: "1px solid #D8D8D8" }}>
              <TableHead>
                <TableRow>
                  {["Sl No", "Critical Risks", "Category", "Risk Score"].map(
                    (header) => (
                      <StyledTableHead key={header}>{header}</StyledTableHead>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rawData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 4, color: "#999", fontSize: "16px" }}
                    >
                      No record found.
                    </TableCell>
                  </TableRow>
                ) : (
                  currentRows.map((row, index) => (
                    <TableRow key={index}>
                      <StyledTableBody>
                        {startIndex + index + 1}
                      </StyledTableBody>
                      <StyledTableBody>{row.riskDescription}</StyledTableBody>
                      <StyledTableBody>{row.package}</StyledTableBody>
                      <StyledTableBody>{row.riskScore}</StyledTableBody>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              padding: "12px 16px",
              backgroundColor: "#fff",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {!isLoading && (
              <PaginationComponent
                pageNumber={currentPage}
                totalPages={totalPages || 1}
                onPageChange={setCurrentPage}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default TopCriticalRisk;
